import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu, Search } from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { apiClient } from '../../packages/api-client';
import { useTheme } from '../../hooks/useTheme';
import { KaziAtlasLogo } from '../KaziAtlasLogo';
import { NotificationBell } from '../NotificationBell';
import { openCommandPalette } from '../CommandPalette';

/** The viewer's own workspace name (GET /tenants/me). Nothing is shown until it loads. */
function useWorkspaceName(): string | undefined {
    const { user } = useAuth();
    const { data } = useQuery({
        queryKey: ['tenants', 'me', user?.tenant_id],
        queryFn: async () => (await apiClient.get<{ name: string }>('/tenants/me')).data,
        enabled: !!user?.tenant_id,
        staleTime: 5 * 60_000,
    });
    return data?.name;
}

export function Topbar({ menuOpen, onOpenMenu }: { menuOpen: boolean; onOpenMenu: () => void }) {
    const workspace = useWorkspaceName();
    const { user } = useAuth();
    const { theme } = useTheme();
    return (
        <header className="shell-topbar">
            <button
                type="button"
                className="shell-icon-btn shell-menu-btn"
                onClick={onOpenMenu}
                aria-label="Open navigation menu"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
            >
                <Menu size={20} aria-hidden="true" />
            </button>
            <span className="shell-topbar-logo">
                <KaziAtlasLogo size={28} onLight={theme === 'light'} />
            </span>
            <div className="shell-workspace">
                {workspace && (
                    <>
                        <span className="sr-only">Workspace: </span>
                        <span className="shell-workspace-name" title={workspace}>{workspace}</span>
                    </>
                )}
            </div>
            <div className="shell-topbar-actions">
                <button type="button" className="shell-search" onClick={openCommandPalette} aria-label="Search" aria-keyshortcuts="Control+K Meta+K">
                    <Search size={16} aria-hidden="true" />
                    <span className="shell-search-label">Search</span>
                    <kbd className="shell-search-kbd" aria-hidden="true">⌘K</kbd>
                </button>
                {/* Keyed on identity: a new user or tenant starts with no count or items. */}
                <NotificationBell key={`${user?.tenant_id ?? ''}:${user?.user_id ?? ''}`} />
            </div>
        </header>
    );
}
